'use client';

import { Pause, Play } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

const VIDEO_DIR = '/landing/video';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Follows `prefers-reduced-motion` for the life of the page. Starts as `true` so the server
 * render and the first client render never autoplay.
 */
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(true);

  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    setReduce(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduce(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduce;
}

interface LoopVideoProps {
  /** File stem in public/landing/video: `<name>.webm`, `<name>.mp4`, `<name>-poster.webp`. */
  name: string;
  className?: string;
  testId: string;
}

/**
 * Muted product loop. The poster is the first paint; the loop starts only after the window `load`
 * event, while in view and without reduced motion. A visitor's pause or play choice wins for the
 * rest of the page view. Render inside a positioned frame: the toggle sits bottom-left over it.
 */
export function LoopVideo({ name, className, testId }: LoopVideoProps) {
  const t = useTranslations('landing.video');
  const videoRef = useRef<HTMLVideoElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [userChoice, setUserChoice] = useState<'play' | 'pause' | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (document.readyState === 'complete') {
      setPageLoaded(true);
      return;
    }
    const onLoad = () => setPageLoaded(true);
    window.addEventListener('load', onLoad, { once: true });
    return () => window.removeEventListener('load', onLoad);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // A batch can hold several entries for the same element; the last one is the current state.
    const observer = new IntersectionObserver(
      (entries) => setInView(entries[entries.length - 1].isIntersecting),
      { threshold: 0.2 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const autoplay = pageLoaded && !reducedMotion && userChoice === null;
  const shouldPlay = inView && (userChoice === 'play' || autoplay);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (shouldPlay) {
      // Rejected when the browser blocks playback (for example low power mode); the poster stays.
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [shouldPlay]);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      setUserChoice('pause');
      video.pause();
    } else {
      setUserChoice('play');
      video.play().catch(() => {});
    }
  };

  return (
    <>
      <video
        ref={videoRef}
        data-testid={testId}
        className={cn('block size-full object-cover', className)}
        muted
        loop
        playsInline
        preload="none"
        poster={`${VIDEO_DIR}/${name}-poster.webp`}
        aria-hidden="true"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      >
        <source src={`${VIDEO_DIR}/${name}.webm`} type='video/webm; codecs="vp9"' />
        <source src={`${VIDEO_DIR}/${name}.mp4`} type="video/mp4" />
      </video>
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        data-state={isPlaying ? 'playing' : 'paused'}
        aria-label={isPlaying ? t('pause') : t('play')}
        onClick={toggle}
        className="absolute bottom-2 left-2 z-30 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {isPlaying ? (
          <Pause className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Play className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
    </>
  );
}
